'use client';

import { c as _c } from "react/compiler-runtime";
import { Gauge } from 'lucide-react';
import type { UIMessage } from 'ai';
import { z } from 'zod';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '~/components/ui/hover-card';

/**
 * AI-SDK-Elements-style "Context" indicator — token usage + estimated cost +
 * context-window progress bar. Rendered only for admin-authed users below each
 * assistant message. Reads `message.metadata` populated by the chat route's
 * `messageMetadata` callback in `toUIMessageStreamResponse`.
 *
 * Pricing & context-window numbers are LIST-price approximations — they're for
 * an at-a-glance dev signal, not an invoicing source. Update when the gateway
 * publishes a final price for a model.
 */

const MessageMetadataSchema = z.object({
  usage: z.object({
    inputTokens: z.number().nullable().optional(),
    outputTokens: z.number().nullable().optional(),
    totalTokens: z.number().nullable().optional()
  }),
  modelId: z.string()
});
type ModelPricing = {
  /** USD per 1M input tokens. */
  inputPerM: number;
  /** USD per 1M output tokens. */
  outputPerM: number;
  /** Context window (tokens). */
  contextWindow: number;
};
const PRICING: Record<string, ModelPricing> = {
  'openai/gpt-oss-120b': {
    inputPerM: 0.15,
    outputPerM: 0.6,
    contextWindow: 128_000
  },
  'openai/gpt-4o-mini': {
    inputPerM: 0.15,
    outputPerM: 0.6,
    contextWindow: 128_000
  },
  'openai/gpt-5-nano': {
    inputPerM: 0.05,
    outputPerM: 0.4,
    contextWindow: 200_000
  },
  'google/gemini-2.0-flash-lite': {
    inputPerM: 0.075,
    outputPerM: 0.3,
    contextWindow: 1_000_000
  },
  'anthropic/claude-sonnet-4-6': {
    inputPerM: 3,
    outputPerM: 15,
    contextWindow: 200_000
  }
};
function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}
function formatUSD(n: number): string {
  if (n < 0.0001) return '<$0.0001';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

/**
 * Aggregate spending across an entire conversation — sums every assistant
 * message's `metadata.usage` and prices it per model. Designed to live in the
 * chat panel's bottom bar (next to the back/forward nav) so admins see total
 * cost without scrolling per-turn badges. The gauge fill reflects the LATEST
 * turn's context-window usage (the meaningful "how full is my context now"
 * signal); the popover lists totals + per-model breakdown.
 */
export const ChatSpendingBadge: React.FC<{
  messages: ReadonlyArray<UIMessage>;
}> = t0 => {
  const $ = _c(66);
  const {
    messages
  } = t0;
  let totalInput = 0;
  let totalOutput = 0;
  let totalCost = 0;
  let hasAnyUsage = false;
  let latestRatio = null;
  let T0;
  let T1;
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== hasAnyUsage || $[1] !== latestRatio || $[2] !== messages || $[3] !== totalCost || $[4] !== totalInput || $[5] !== totalOutput) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let latestModel = null;
      const perModel = new Map();
      for (const m of messages) {
        if (m.role !== "assistant") {
          continue;
        }
        const parsed = MessageMetadataSchema.safeParse(m.metadata);
        if (!parsed.success) {
          continue;
        }
        const {
          usage,
          modelId
        } = parsed.data;
        const input = usage.inputTokens ?? 0;
        const output = usage.outputTokens ?? 0;
        const total = usage.totalTokens ?? input + output;
        if (total === 0) {
          continue;
        }
        hasAnyUsage = true;
        totalInput = totalInput + input;
        totalOutput = totalOutput + output;
        const pricing = PRICING[modelId];
        const cost = pricing ? input / 1000000 * pricing.inputPerM + output / 1000000 * pricing.outputPerM : 0;
        totalCost = totalCost + cost;
        const slot = perModel.get(modelId) ?? {
          input: 0,
          output: 0,
          cost: 0
        };
        slot.input = slot.input + input;
        slot.output = slot.output + output;
        slot.cost = slot.cost + cost;
        perModel.set(modelId, slot);
        if (pricing) {
          latestRatio = Math.min(1, total / pricing.contextWindow);
        }
        latestModel = modelId;
      }
      if (!hasAnyUsage) {
        t10 = null;
        break bb0;
      }
      const gaugeFill = latestRatio ?? 0;
      const latestPricing = latestModel ? PRICING[latestModel] : undefined;
      T1 = HoverCard;
      const t11 = 0.4 + gaugeFill * 0.6;
      if ($[23] !== t11) {
        t9 = <HoverCardTrigger asChild={true}><button type="button" data-testid="ai-chat-spending-badge-trigger" aria-label="Total token usage and cost" className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-blue-900/50 transition-colors hover:bg-blue-50 hover:text-blue-900"><Gauge className="size-3.5" strokeWidth={2} style={{
              opacity: t11
            }} aria-hidden={true} /></button></HoverCardTrigger>;
        $[23] = t11;
        $[24] = t9;
      } else {
        t9 = $[24];
      }
      T0 = HoverCardContent;
      t1 = "ai-chat-spending-badge";
      t2 = "top";
      t3 = "start";
      t4 = "flex w-72 flex-col gap-2 text-[10px] text-blue-900/70";
      let t12;
      if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = <span className="font-medium text-blue-900">Conversation total</span>;
        $[25] = t12;
      } else {
        t12 = $[25];
      }
      let t13;
      if ($[26] !== latestPricing || $[27] !== latestRatio) {
        t13 = latestPricing && latestRatio !== null ? <span title="Current turn's share of the model context window">{(latestRatio * 100).toFixed(1)}% of{" "}{formatTokens(latestPricing.contextWindow)}</span> : null;
        $[26] = latestPricing;
        $[27] = latestRatio;
        $[28] = t13;
      } else {
        t13 = $[28];
      }
      if ($[29] !== t13) {
        t5 = <div className="flex items-center justify-between gap-2 font-mono">{t12}{t13}</div>;
        $[29] = t13;
        $[30] = t5;
      } else {
        t5 = $[30];
      }
      if ($[31] !== latestPricing || $[32] !== latestRatio) {
        t6 = latestPricing && latestRatio !== null ? <div className="h-1 w-full overflow-hidden rounded-full bg-blue-900/10"><div className="h-full rounded-full bg-secondary/60" style={{
            width: `${Math.max(0.4, latestRatio * 100)}%`
          }} aria-hidden={true} /></div> : null;
        $[31] = latestPricing;
        $[32] = latestRatio;
        $[33] = t6;
      } else {
        t6 = $[33];
      }
      let t14;
      if ($[34] !== totalInput) {
        t14 = formatTokens(totalInput);
        $[34] = totalInput;
        $[35] = t14;
      } else {
        t14 = $[35];
      }
      let t15;
      if ($[36] !== t14) {
        t15 = <span>{t14} in</span>;
        $[36] = t14;
        $[37] = t15;
      } else {
        t15 = $[37];
      }
      let t16;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = <span aria-hidden={true}>·</span>;
        $[38] = t16;
      } else {
        t16 = $[38];
      }
      let t17;
      if ($[39] !== totalOutput) {
        t17 = formatTokens(totalOutput);
        $[39] = totalOutput;
        $[40] = t17;
      } else {
        t17 = $[40];
      }
      let t18;
      if ($[41] !== t17) {
        t18 = <span>{t17} out</span>;
        $[41] = t17;
        $[42] = t18;
      } else {
        t18 = $[42];
      }
      let t19;
      if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = <span aria-hidden={true}>·</span>;
        $[43] = t19;
      } else {
        t19 = $[43];
      }
      let t20;
      if ($[44] !== totalCost) {
        t20 = formatUSD(totalCost);
        $[44] = totalCost;
        $[45] = t20;
      } else {
        t20 = $[45];
      }
      let t21;
      if ($[46] !== t20) {
        t21 = <span title="Estimated, list-price approximation">~{t20}</span>;
        $[46] = t20;
        $[47] = t21;
      } else {
        t21 = $[47];
      }
      if ($[48] !== t15 || $[49] !== t18 || $[50] !== t21) {
        t7 = <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono">{t15}{t16}{t18}{t19}{t21}</div>;
        $[48] = t15;
        $[49] = t18;
        $[50] = t21;
        $[51] = t7;
      } else {
        t7 = $[51];
      }
      t8 = perModel.size > 1 ? <ul className="mt-1 flex flex-col gap-0.5 border-t border-blue-900/10 pt-1.5 font-mono">{[...perModel.entries()].map(_temp)}</ul> : null;
    }
    $[0] = hasAnyUsage;
    $[1] = latestRatio;
    $[2] = messages;
    $[3] = totalCost;
    $[4] = totalInput;
    $[5] = totalOutput;
    $[6] = T0;
    $[7] = T1;
    $[8] = t1;
    $[9] = t10;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = hasAnyUsage;
    $[19] = totalInput;
    $[20] = totalOutput;
    $[21] = totalCost;
    $[22] = latestRatio;
  } else {
    T0 = $[6];
    T1 = $[7];
    t1 = $[8];
    t10 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    t9 = $[17];
    hasAnyUsage = $[18];
    totalInput = $[19];
    totalOutput = $[20];
    totalCost = $[21];
    latestRatio = $[22];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[52] !== T0 || $[53] !== t1 || $[54] !== t2 || $[55] !== t3 || $[56] !== t4 || $[57] !== t5 || $[58] !== t6 || $[59] !== t7 || $[60] !== t8) {
    t11 = <T0 data-testid={t1} side={t2} align={t3} className={t4}>{t5}{t6}{t7}{t8}</T0>;
    $[52] = T0;
    $[53] = t1;
    $[54] = t2;
    $[55] = t3;
    $[56] = t4;
    $[57] = t5;
    $[58] = t6;
    $[59] = t7;
    $[60] = t8;
    $[61] = t11;
  } else {
    t11 = $[61];
  }
  let t12;
  if ($[62] !== T1 || $[63] !== t11 || $[64] !== t9) {
    t12 = <T1>{t9}{t11}</T1>;
    $[62] = T1;
    $[63] = t11;
    $[64] = t9;
    $[65] = t12;
  } else {
    t12 = $[65];
  }
  return t12;
};
export const ChatContextBadge: React.FC<{
  metadata: unknown;
}> = t0 => {
  const $ = _c(48);
  const {
    metadata
  } = t0;
  let T0;
  let T1;
  let costUsd;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== metadata) {
    t12 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const parsed = MessageMetadataSchema.safeParse(metadata);
      if (!parsed.success) {
        t12 = null;
        break bb0;
      }
      const {
        usage,
        modelId
      } = parsed.data;
      const inputTokens = usage.inputTokens ?? 0;
      const outputTokens = usage.outputTokens ?? 0;
      const totalTokens = usage.totalTokens ?? inputTokens + outputTokens;
      if (totalTokens === 0) {
        t12 = null;
        break bb0;
      }
      const pricing = PRICING[modelId];
      costUsd = pricing ? inputTokens / 1000000 * pricing.inputPerM + outputTokens / 1000000 * pricing.outputPerM : null;
      const contextWindow = pricing?.contextWindow ?? null;
      const usedRatio = contextWindow ? Math.min(1, totalTokens / contextWindow) : null;
      const usedPercent = usedRatio !== null ? Math.max(0.4, usedRatio * 100) : null;
      const gaugeFill = usedRatio ?? 0;
      T1 = HoverCard;
      const t13 = 0.4 + gaugeFill * 0.6;
      if ($[16] !== t13) {
        t11 = <HoverCardTrigger asChild={true}><button type="button" data-testid="ai-chat-context-badge-trigger" aria-label="Show token usage and cost" className="group/ctx inline-flex size-5 shrink-0 items-center justify-center self-start rounded-full text-blue-900/40 transition-colors hover:bg-blue-50 hover:text-blue-900/80"><Gauge className="size-3.5" strokeWidth={2} style={{
              opacity: t13
            }} aria-hidden={true} /></button></HoverCardTrigger>;
        $[16] = t13;
        $[17] = t11;
      } else {
        t11 = $[17];
      }
      T0 = HoverCardContent;
      t5 = "ai-chat-context-badge";
      t6 = "top";
      t7 = "start";
      t8 = "flex w-64 flex-col gap-1.5 text-[10px] text-blue-900/70";
      let t14;
      if ($[18] !== contextWindow || $[19] !== usedPercent || $[20] !== usedRatio) {
        t14 = usedPercent !== null ? <span>{usedRatio < 0.001 ? "<0.1" : (usedRatio * 100).toFixed(1)}% of{" "}{formatTokens(contextWindow)}</span> : null;
        $[18] = contextWindow;
        $[19] = usedPercent;
        $[20] = usedRatio;
        $[21] = t14;
      } else {
        t14 = $[21];
      }
      t9 = <div className="flex items-center justify-between gap-2 font-mono"><span className="truncate text-blue-900" title={modelId}>{modelId.replace(/^[^/]+\//, "")}</span>{t14}</div>;
      if ($[22] !== usedPercent) {
        t10 = usedPercent !== null ? <div className="h-1 w-full overflow-hidden rounded-full bg-blue-900/10"><div className="h-full rounded-full bg-secondary/60" style={{
            width: `${usedPercent}%`
          }} aria-hidden={true} /></div> : null;
        $[22] = usedPercent;
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      t2 = "flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono";
      t3 = <span>{formatTokens(inputTokens)} in</span>;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <span aria-hidden={true}>·</span>;
        $[24] = t4;
      } else {
        t4 = $[24];
      }
      t1 = formatTokens(outputTokens);
    }
    $[0] = metadata;
    $[1] = T0;
    $[2] = T1;
    $[3] = costUsd;
    $[4] = t1;
    $[5] = t10;
    $[6] = t11;
    $[7] = t12;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    T0 = $[1];
    T1 = $[2];
    costUsd = $[3];
    t1 = $[4];
    t10 = $[5];
    t11 = $[6];
    t12 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  if (t12 !== Symbol.for("react.early_return_sentinel")) {
    return t12;
  }
  let t13;
  if ($[25] !== t1) {
    t13 = <span>{t1} out</span>;
    $[25] = t1;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== costUsd) {
    t14 = costUsd !== null ? <><span aria-hidden={true}>·</span><span title="Estimated, list-price approximation">~{formatUSD(costUsd)}</span></> : null;
    $[27] = costUsd;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t13 || $[30] !== t14 || $[31] !== t2 || $[32] !== t3 || $[33] !== t4) {
    t15 = <div className={t2}>{t3}{t4}{t13}{t14}</div>;
    $[29] = t13;
    $[30] = t14;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== T0 || $[36] !== t10 || $[37] !== t15 || $[38] !== t5 || $[39] !== t6 || $[40] !== t7 || $[41] !== t8 || $[42] !== t9) {
    t16 = <T0 data-testid={t5} side={t6} align={t7} className={t8}>{t9}{t10}{t15}</T0>;
    $[35] = T0;
    $[36] = t10;
    $[37] = t15;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== T1 || $[45] !== t11 || $[46] !== t16) {
    t17 = <T1>{t11}{t16}</T1>;
    $[44] = T1;
    $[45] = t11;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  return t17;
};
function _temp(t0) {
  const [id, slot_0] = t0;
  return <li key={id} className="flex items-center justify-between gap-2"><span className="truncate" title={id}>{id.replace(/^[^/]+\//, "")}</span><span>{formatTokens(slot_0.input + slot_0.output)} · ~{formatUSD(slot_0.cost)}</span></li>;
}